<script>
  import { t } from '../../i18n/index.js';
  import { applyFilter } from '../../image-processor.js';
  import { applyPerspectiveCorrection } from '../../document-scanner.js';
  import { loadOpenCV, isOpenCVReady } from '../../opencv-loader.js';
  import CornerEditor from './CornerEditor.svelte';

  let { page, onSave, onCancel } = $props();

  // State
  let selectedFilter = $state('gray');
  let currentCorners = $state(null);
  let processing = $state(false);

  // Initialize state when page changes
  $effect(() => {
    selectedFilter = page.filter || 'gray';
    currentCorners = page.corners ? JSON.parse(JSON.stringify(page.corners)) : null;
  });

  const filters = ['bw', 'gray', 'color'];

  // Check if we have raw image and corners for corner editing
  const canEditCorners = $derived(!!page.rawImage && !!currentCorners);

  function handleCornersChange(newCorners) {
    currentCorners = newCorners;
  }

  async function handleSave() {
    processing = true;

    try {
      let fullImage = page.fullImage;
      let thumbnail;

      // If we can edit corners, re-apply perspective correction
      if (canEditCorners && page.rawImage) {
        // Load OpenCV if needed
        if (!isOpenCVReady()) {
          await loadOpenCV(() => {});
        }

        // Apply perspective correction with current corners (auto-calculates size from corners)
        fullImage = await applyPerspectiveCorrection(page.rawImage, currentCorners);
      }

      // Apply selected filter
      thumbnail = await applyFilter(fullImage, selectedFilter);

      onSave?.({
        filter: selectedFilter,
        thumbnail,
        corners: currentCorners,
        fullImage
      });
    } catch (e) {
      console.error('Save failed:', e);
      // Fallback - save without reprocessing
      onSave?.({
        filter: selectedFilter,
        thumbnail: page.thumbnail,
        corners: currentCorners
      });
    } finally {
      processing = false;
    }
  }
</script>

<div class="modal-overlay">
  <div class="modal-content">
    <!-- Editor area -->
    <div class="editor-container">
      {#if canEditCorners}
        <CornerEditor
          rawImage={page.rawImage}
          corners={currentCorners}
          onCornersChange={handleCornersChange}
        />
      {:else}
        <img src={page.thumbnail} alt="Page preview" />
      {/if}

      {#if processing}
        <div class="processing-overlay">
          <div class="spinner"></div>
        </div>
      {/if}
    </div>

    <!-- Filter buttons -->
    <div class="filter-buttons">
      {#each filters as filter}
        <button
          class="filter-btn {selectedFilter === filter ? 'active' : ''}"
          onclick={() => selectedFilter = filter}
          disabled={processing}
        >
          {t(`filter.${filter}`)}
        </button>
      {/each}
    </div>

    <!-- Action buttons -->
    <div class="action-buttons">
      <button
        onclick={onCancel}
        class="btn-secondary"
        disabled={processing}
      >
        {t('filter.retake')}
      </button>
      <button
        onclick={handleSave}
        disabled={processing}
        class="btn-primary"
      >
        {t('filter.confirm')}
      </button>
    </div>
  </div>
</div>

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    flex-direction: column;
    z-index: 100;
  }

  .modal-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .editor-container {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    overflow: hidden;
  }

  .editor-container img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .processing-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(59, 130, 246, 0.2);
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .filter-buttons {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 16px;
  }

  .filter-btn {
    flex: 1;
    max-width: 100px;
    padding: 14px 16px;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 500;
    color: #9ca3af;
    background: #374151;
    border: 2px solid transparent;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .filter-btn:hover {
    background: #4b5563;
  }

  .filter-btn:active {
    transform: scale(0.96);
  }

  .filter-btn.active {
    background: #1e3a5f;
    border-color: #3b82f6;
    color: white;
    font-weight: 600;
  }

  .filter-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .action-buttons {
    display: flex;
    gap: 16px;
    padding: 16px;
    padding-bottom: max(16px, env(safe-area-inset-bottom));
    background: rgba(17, 24, 39, 0.95);
  }

  .btn-secondary {
    flex: 1;
    min-height: 60px;
    padding: 16px 24px;
    background: #374151;
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-secondary:hover {
    background: #4b5563;
  }

  .btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-primary {
    flex: 1;
    min-height: 60px;
    padding: 20px 24px;
    background: #2563eb;
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1.25rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-primary:hover {
    background: #3b82f6;
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
